import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, Timestamp, orderBy, getDocs, getDoc, limit, doc, setDoc, serverTimestamp, startAfter } from 'firebase/firestore';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { db, auth } from '../../../../firebase'; // Adjust path as needed
import { FeedEmailData } from '../components/EmailFeedContent'; // Assuming FeedEmailData is exported
import { EmailDraft, draftService } from '../../../services/draftService'; // Import draft service
import { listenToMailSourceMode, normalizeMailSource, type MailSource, type MailSourceMode } from '../../../utils/mailSourceConfig';
import { playNewEmailSound } from '../../../utils/emailSound';
import { getEmailFlaggedState } from '../utils/emailFlagState';
import { normalizeEmailFolder, type EmailFolder } from '../utils/emailFolder';
import {
  type FeedCoordinator,
  appendOlderEmails,
  createFeedCoordinator,
  draftRecencyMs,
  mergeWithExternal,
  paginationEdge,
  resolveFeedMailboxes,
  sameMailboxSet,
  sortNewestFirstBy,
  thirtyDayCutoff,
  unionWindowEmails,
  DEFAULT_FEED_MAILBOXES,
} from '../utils/feedWindow';

function normalizeEmailAddr(value: unknown): string {
  if (!value) return ''
  return String(value).trim().toLowerCase()
}

function parseAddressFromText(value: unknown): string {
  const s = String(value || '').trim()
  if (!s) return ''
  const m = s.match(/<([^>]+)>/)
  if (m && m[1]) return normalizeEmailAddr(m[1])
  if (s.includes('@')) return normalizeEmailAddr(s)
  return ''
}

// Define the types for the context
export type EmailPanelView = 'feed' | 'viewEmail' | 'composeEmail' | 'drafts' | 'editDraft';

export interface GraphSyncJob {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  progress?: string;
  percent?: number;
  error?: string;
  logs?: string[];
  createdAt: any;
  updatedAt: any;
}

// Classification categories for sender-level filtering
export type SenderClassification = 'client' | 'personal' | 'logs' | 'junk';

// Define a consistent AttachmentData interface
export interface AttachmentData {
  filename: string;
  url?: string;         // For GCS stored attachments (received emails)
  content?: string;     // For Base64 encoded attachments (newly composed/sent emails)
  contentType?: string;
  cid?: string;         // For inline images
}

// Define the structure for email data that might be passed around
export interface PanelEmailData {
  id?: string; // Firestore document ID
  to?: string | string[];
  cc?: string | string[]; // Added CC field
  bcc?: string | string[]; // Added BCC field
  from?: { address: string; name?: string; text?: string }[];
  subject?: string;
  html?: string | null;
  text?: string | null;
  date?: any; // Firestore Timestamp or Date
  isReply?: boolean;
  isForward?: boolean;
  /** When true: treat as reply, but also copy original to+cc into the
   *  composer's cc field (minus the sender, who lands in to). */
  isReplyAll?: boolean;
  attachments?: AttachmentData[]; // Use the consistent AttachmentData type
  customerId?: string | null;   // Add customerId
  source?: MailSource;          // 'imap' | 'graph' (missing treated as 'imap')
  isRead?: boolean;             // Graph read state (mirrored into dismissed for now)
  mailbox?: string;             // For graph emails: the mailbox userPrincipalName
  graph?: { id?: string | null; conversationId?: string | null; flagStatus?: string | null }; // Minimal Graph metadata
  hasAttachments?: boolean;
  folder?: EmailFolder; // Logical folder for new tab system ('archive' / 'filed' = moved out of the inbox in Outlook)
  aiSummary?: string | null;        // one-line LLM summary (written at ingestion)
  aiImportance?: string | null;     // e.g. 'high' | 'normal' | 'low'
  linkedJobId?: string | null;      // job this email is attached to (for reply-threads-to-timeline)
  // Who-sent-it signals. The composer needs them to tell an email the owner SENT
  // (reply goes to the original recipients) from one they received (reply goes to
  // the sender); `folder` alone misses own copies that landed in inbox/deleted.
  emailType?: 'inbox' | 'sent';     // derived by convertDocToFeedEmailData (direction 'out' => 'sent')
  direction?: string;               // raw ingest direction ('in' | 'out' | ...)
  senderEmail?: string;             // lower-cased primary from address
}

export interface CustomMailFolder {
  id: string;
  name: string;
  senderEmails: string[];
  color?: string;
  updatedAt?: any;
  deleted?: boolean;
  // One-level nesting support (Option A): groups are non-clickable headers that contain child folders.
  // Only leaf folders should contain senderEmails rules.
  parentId?: string | null;
  isGroup?: boolean;
}

interface EmailPanelContextType {
  isEmailPanelOpen: boolean;
  emailPanelView: EmailPanelView;
  currentEmailData: PanelEmailData | null;
  openEmailPanel: (view: EmailPanelView, data?: PanelEmailData) => void;
  closeEmailPanel: () => void;
  setEmailPanelView: (view: EmailPanelView, data?: PanelEmailData) => void; // Allow passing data when switching views

  // New state for proactive email fetching and badge count
  preLoadedEmails: FeedEmailData[]; // Emails for initial feed display
  unreadBadgeCount: number;       // Count for the notification badge
  isLoadingInitialEmails: boolean;

  // New state for loading older emails
  isLoadingOlderEmails: boolean;
  hasMoreOlderEmails: boolean;
  loadOlderEmails: () => Promise<void>;

  // New function for loading emails in a specific date range
  loadEmailsForDateRange: (startDate: Date, endDate: Date, customerId?: string) => Promise<FeedEmailData[]>;
  
  // Load specific emails by their Firestore document IDs (for search results)
  loadEmailsByIds: (emailIds: string[]) => Promise<FeedEmailData[]>;

  // ── Search state — lifted to context so it survives across feed
  // mount/unmount (closing the feed or opening an email modal would
  // otherwise reset the search term + results). Reset only when the
  // user explicitly clears the term.
  searchTerm: string;
  setSearchTerm: (term: string) => void;

  // Server search snapshot. `deepSearchIds` is the ID set Graph
  // returned for `lastDeepSearchTerm`; consumers union it with the
  // local tokenized filter so a row matching either survives.
  isDeepSearching: boolean;
  setIsDeepSearching: (v: boolean) => void;
  lastDeepSearchTerm: string;
  setLastDeepSearchTerm: (v: string) => void;
  deepSearchHits: number | null;
  setDeepSearchHits: (v: number | null) => void;
  deepSearchIds: Set<string> | null;
  setDeepSearchIds: (v: Set<string> | null) => void;

  // The set of email IDs currently covered by the live inbox snapshot
  // (the canonical 30-day Firestore subscription). This is an ALLOWLIST,
  // replaced wholesale on every snapshot tick. Consumers that must reflect
  // the live inbox — the dashboard attention queue and the unread badge —
  // gate on membership here so transient search / historical hydration
  // (loadEmailsByIds, loadEmailsForDateRange) merged into preLoadedEmails
  // can never pollute them. Self-correcting: an email entering or leaving
  // the live window is reflected on the next snapshot automatically.
  liveInboxIds: Set<string>;

  // Which mail pipeline is active for UI filtering
  mailSourceMode: MailSourceMode;

  // Sender-level classification map and updater
  senderClassifications: Record<string, SenderClassification>;
  setSenderClassification: (senderEmail: string, classification: SenderClassification) => void;

  // Custom Virtual Folders
  customFolders: CustomMailFolder[];
  createCustomFolder: (name: string, options?: { parentId?: string | null; isGroup?: boolean }) => Promise<string>;
  addSenderToFolder: (folderId: string, senderEmail: string) => Promise<void>;
  removeSenderFromFolder: (folderId: string, senderEmail: string) => Promise<void>;
  renameCustomFolder: (folderId: string, name: string) => Promise<void>;
  setCustomFolderParent: (folderId: string, parentId: string | null) => Promise<void>;
  deleteCustomFolder: (folderId: string) => Promise<void>;

  // Draft management state and functions
  currentDraft: EmailDraft | null;
  draftMode: 'new' | 'edit' | 'none';
  userDrafts: EmailDraft[];
  isLoadingDrafts: boolean;
  draftError: string | null;

  // Draft operations
  saveDraft: (draftData: Partial<EmailDraft>) => Promise<string>;
  updateDraft: (draftId: string, updates: Partial<EmailDraft>) => Promise<void>;
  deleteDraft: (draftId: string) => Promise<void>;
  loadUserDrafts: () => Promise<void>;
  setDraftMode: (mode: 'new' | 'edit' | 'none', draft?: EmailDraft) => void;
  clearDraftError: () => void;

  // Active sync job status
  activeSyncJob: GraphSyncJob | null;
}

const EmailPanelContext = createContext<EmailPanelContextType | undefined>(undefined);

// Module-level (pure) so the feed subscription effect can call it without listing it as a dependency.
const filterEmailsBySourceMode = (emails: FeedEmailData[], mode: MailSourceMode): FeedEmailData[] => {
  if (mode === 'both') return emails;
  if (mode === 'graph') return emails.filter((e) => (e.source || 'imap') === 'graph');
  // 'imap' mode: include legacy docs with no source and explicit imap, exclude graph
  return emails.filter((e) => (e.source || 'imap') !== 'graph');
};

// -- Drafts ---------------------------------------------------------------------------------------
// The drafts list is an equality-only query sorted CLIENT-side. `orderBy('updatedAt')` silently
// excluded Outlook-synced drafts (their docs can lack updatedAt: they carry syncedAt/createdAt),
// and every read path must agree or the last writer wins and drops them again.
const draftsQueryForUser = (userId: string) =>
  query(collection(db, 'email_drafts'), where('userId', '==', userId), where('status', '==', 'draft'));

// First Timestamp-like value (Timestamp has toDate); stops a synced draft reading as "updated now".
const firstTimestampLike = (...values: any[]): any =>
  values.find((v) => v && typeof v.toDate === 'function');

const mapDraftDocs = (docs: QueryDocumentSnapshot<DocumentData>[]): EmailDraft[] => {
  const rows = docs.map((d) => {
    const data = d.data() as any;
    const draft: EmailDraft = {
      id: d.id,
      userId: data.userId,
      subject: data.subject || '',
      htmlBody: data.htmlBody || '',
      textBody: data.textBody || '',
      to: Array.isArray(data.to) ? data.to : [],
      cc: Array.isArray(data.cc) ? data.cc : [],
      bcc: Array.isArray(data.bcc) ? data.bcc : [],
      attachments: data.attachments || [],
      senderAccountId: data.senderAccountId || '',
      replyToEmailId: data.replyToEmailId,
      isReply: data.isReply || false,
      isForward: data.isForward || false,
      createdAt: data.createdAt || Timestamp.now(),
      updatedAt: data.updatedAt || firstTimestampLike(data.syncedAt, data.createdAt) || Timestamp.now(),
      status: data.status || 'draft',
      lastSavedAt: data.lastSavedAt,
      wordCount: data.wordCount || 0,
      source: data.source,
      graphMailbox: data.graphMailbox,
      graphMessageId: data.graphMessageId,
      // Carry the invoice link so sending an auto-drafted recurring-invoice
      // email marks the invoice SENT (SendEmailForm reads currentDraft here).
      linkedInvoiceId: data.linkedInvoiceId || undefined,
      // Forward context persisted by the composer: without these a consumer of
      // currentDraft re-includes original attachments the owner had removed.
      // Same shape rules as draftService (strings only, blanks dropped, cap 200;
      // an EMPTY list is kept - it records that the owner pressed [restore]).
      forwardFromGraphId: typeof data.forwardFromGraphId === 'string' && data.forwardFromGraphId ? data.forwardFromGraphId : undefined,
      forwardExcludeNames: Array.isArray(data.forwardExcludeNames)
        ? data.forwardExcludeNames.filter((n: unknown): n is string => typeof n === 'string' && n.trim().length > 0).slice(0, 200)
        : undefined,
    };
    return { draft, ms: draftRecencyMs(data) };
  });
  return sortNewestFirstBy(rows, (r) => r.ms).map((r) => r.draft);
};

const readUserDraftsLocal = async (userId: string): Promise<EmailDraft[]> => {
  try {
    const snap = await getDocs(draftsQueryForUser(userId));
    return mapDraftDocs(snap.docs);
  } catch (error) {
    console.error('Error getting user drafts:', error);
    throw new Error('Failed to retrieve email drafts');
  }
};

export const EmailPanelProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [isEmailPanelOpen, setIsEmailPanelOpen] = useState(false);
  const [emailPanelView, setEmailPanelViewState] = useState<EmailPanelView>('feed'); // Renamed for clarity
  const [currentEmailData, setCurrentEmailData] = useState<PanelEmailData | null>(null);

  // Draft management state
  const [currentDraft, setCurrentDraft] = useState<EmailDraft | null>(null);
  const [draftMode, setDraftModeState] = useState<'new' | 'edit' | 'none'>('none');
  const [userDrafts, setUserDrafts] = useState<EmailDraft[]>([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  // Guard ref: prevents concurrent loadUserDrafts calls from overwriting each other
  const isLoadingDraftsRef = useRef(false);

  // New state for emails and badge
  const [preLoadedEmails, setPreLoadedEmails] = useState<FeedEmailData[]>([]);
  const [unreadBadgeCount, setUnreadBadgeCount] = useState<number>(0);
  const prevUnreadBadgeCountRef = React.useRef<number | null>(null);
  const skipNextUnreadSoundRef = React.useRef<boolean>(true);
  const [isLoadingInitialEmails, setIsLoadingInitialEmails] = useState<boolean>(true);
  
  // New state for loading older emails
  const [isLoadingOlderEmails, setIsLoadingOlderEmails] = useState<boolean>(false);
  const [hasMoreOlderEmails, setHasMoreOlderEmails] = useState<boolean>(true);
  const [oldestLoadedDate, setOldestLoadedDate] = useState<Timestamp | null>(null);

  // ── Lifted search state (persists across feed mount/unmount) ────
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isDeepSearching, setIsDeepSearching] = useState<boolean>(false);
  const [lastDeepSearchTerm, setLastDeepSearchTerm] = useState<string>('');
  const [deepSearchHits, setDeepSearchHits] = useState<number | null>(null);
  const [deepSearchIds, setDeepSearchIds] = useState<Set<string> | null>(null);
  // IDs that arrived via search hydration. Excluded from the dashboard
  // attention queue so a search doesn't pollute "Waiting For You".
  const [liveInboxIds, setLiveInboxIds] = useState<Set<string>>(() => new Set());

  // Active sync job state
  const [activeSyncJob, setActiveSyncJob] = useState<GraphSyncJob | null>(null);

  // Custom Virtual Folders state
  const [customFolders, setCustomFolders] = useState<CustomMailFolder[]>([]);

  // Mail source mode (imap | graph | both). Defaults to 'both' for safe transition.
  const [mailSourceMode, setMailSourceModeState] = useState<MailSourceMode>('both');

  // Mailboxes the feed listens to (one listener each). Seeded with the default list so the feed
  // starts immediately; system_config/mail_source then refines it (only listeners for mailboxes
  // that actually changed are re-subscribed).
  const [feedMailboxes, setFeedMailboxes] = useState<string[]>(() => DEFAULT_FEED_MAILBOXES.slice());
  const feedMailboxesRef = useRef<string[]>(feedMailboxes);
  feedMailboxesRef.current = feedMailboxes;
  const feedCoordinatorRef = useRef<FeedCoordinator | null>(null);
  // Latched once a per-mailbox listener fails: the rest of the session uses the legacy single query.
  const feedLegacyFallbackRef = useRef<boolean>(false);

  useEffect(() => {
    const unsub = listenToMailSourceMode((mode, config) => {
      setMailSourceModeState(mode);
      // `config` is absent after a read error: keep the list we already have.
      if (config) {
        const next = resolveFeedMailboxes(config.mailboxes);
        setFeedMailboxes((prev) => (sameMailboxSet(prev, next) ? prev : next));
      }
    });
    return () => unsub();
  }, []);

  // Real-time listener for the most recent active sync job
  useEffect(() => {
    // Listen for jobs created in the last 10 minutes.
    // We intentionally do NOT filter by status in the query to avoid requiring new composite indexes.
    // Instead, we pull a small window of recent jobs and pick the newest one that is still active.
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    const q = query(
      collection(db, 'graph_sync_jobs'),
      where('createdAt', '>=', Timestamp.fromDate(tenMinutesAgo)),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) return setActiveSyncJob(null);

      const newestActive = snap.docs.find((d) => {
        const data = d.data() as any;
        return data?.status === 'pending' || data?.status === 'in_progress';
      });

      if (!newestActive) return setActiveSyncJob(null);

      const data = newestActive.data() as any;
      setActiveSyncJob({ id: newestActive.id, ...data } as GraphSyncJob);
    });

    return () => unsub();
  }, []);

  // Real-time Firestore listener for custom folders
  useEffect(() => {
    const q = query(collection(db, 'mail_custom_folders'), orderBy('name', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      // Filter out soft-deleted folders client-side (avoids needing Firestore inequality indexes).
      const folders = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) } as CustomMailFolder))
        .filter((f) => f.deleted !== true);
      setCustomFolders(folders);
    }, (err) => console.error('[EmailPanelContext] custom folders listener error:', err));
    return () => unsub();
  }, []);

  // Sender-level classifications persisted in localStorage (per-device, no backend changes)
  const [senderClassifications, setSenderClassifications] = useState<Record<string, SenderClassification>>(() => {
    try {
      const raw = localStorage.getItem('emailSenderClassifications');
      return raw ? (JSON.parse(raw) as Record<string, SenderClassification>) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('emailSenderClassifications', JSON.stringify(senderClassifications));
    } catch {}
  }, [senderClassifications]);

  // Real-time Firestore listener for shared sender classifications
  useEffect(() => {
    const ref = collection(db, 'email_sender_classifications');
    const unsub = onSnapshot(ref, (snap) => {
      const next: Record<string, SenderClassification> = {};
      snap.forEach((d) => {
        const data = d.data() as any;
        const key = (data.senderEmail || d.id || '').toLowerCase();
        const cls = data.classification as SenderClassification | undefined;
        if (key && (cls === 'client' || cls === 'personal' || cls === 'logs' || cls === 'junk')) {
          next[key] = cls;
          // Backward compatibility: if key accidentally stored with direction prefix, also map the raw address
          if (key.startsWith('in:') || key.startsWith('out:')) {
            const raw = key.replace(/^in:|^out:/, '');
            if (raw) {
              next[raw] = cls;
            }
          }
        }
      });
      setSenderClassifications((prev) => ({ ...prev, ...next }));
    }, (err) => {
      console.error('[EmailPanelContext] sender classifications listener error:', err);
    });
    return () => unsub();
  }, []);

  const setSenderClassification = async (senderEmail: string, classification: SenderClassification) => {
    const key = (senderEmail || '').trim().toLowerCase();
    if (!key) return;
    // Optimistic local update
    setSenderClassifications(prev => ({ ...prev, [key]: classification }));
    try {
      await setDoc(doc(db, 'email_sender_classifications', key), {
        senderEmail: key,
        classification,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.error('[EmailPanelContext] Failed to persist sender classification', e);
    }
  };

  const createCustomFolder = async (
    name: string,
    options?: { parentId?: string | null; isGroup?: boolean }
  ): Promise<string> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Folder name cannot be empty');
    const folderId = `f_${Date.now()}`;
    const isGroup = options?.isGroup === true;
    const parentId = isGroup ? null : (options?.parentId ?? null);
    await setDoc(doc(db, 'mail_custom_folders', folderId), {
      name: trimmed,
      senderEmails: [],
      isGroup,
      parentId,
      updatedAt: serverTimestamp(),
    });
    return folderId;
  };

  const addSenderToFolder = async (folderId: string, senderEmail: string) => {
    const email = senderEmail.trim().toLowerCase();
    if (!email) return;
    const folder = customFolders.find(f => f.id === folderId);
    if (!folder) return;
    // Groups are headers only; do not allow rules to be added.
    if (folder.isGroup === true) return;
    if (folder.senderEmails.includes(email)) return;

    await setDoc(doc(db, 'mail_custom_folders', folderId), {
      senderEmails: [...folder.senderEmails, email],
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const removeSenderFromFolder = async (folderId: string, senderEmail: string) => {
    const email = senderEmail.trim().toLowerCase();
    const folder = customFolders.find(f => f.id === folderId);
    if (!folder) return;
    if (folder.isGroup === true) return;

    await setDoc(doc(db, 'mail_custom_folders', folderId), {
      senderEmails: folder.senderEmails.filter(e => e !== email),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const renameCustomFolder = async (folderId: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Folder name cannot be empty');
    await setDoc(doc(db, 'mail_custom_folders', folderId), {
      name: trimmed,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const setCustomFolderParent = async (folderId: string, parentId: string | null) => {
    await setDoc(doc(db, 'mail_custom_folders', folderId), {
      parentId: parentId || null,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const deleteCustomFolder = async (folderId: string) => {
    // Basic delete; potentially we could use a batch to clean up rules
    await setDoc(doc(db, 'mail_custom_folders', folderId), {
      deleted: true, // soft delete or actual delete
      updatedAt: serverTimestamp(),
    }, { merge: true });
    // Or actual delete: await deleteDoc(doc(db, 'mail_custom_folders', folderId));
  };

  // Helper function to convert Firestore document to FeedEmailData
  const convertDocToFeedEmailData = (docSnap: any): FeedEmailData => {
    const data = docSnap.data();
    // Prefer canonical field if present; fallback to simple heuristic
    let emailType: 'inbox' | 'sent' = (data.emailType === 'sent' || data.direction === 'out') ? 'sent' : 'inbox';
    
    // Normalize date to Firestore Timestamp - handle various formats
    let timestamp: Timestamp;
    if (data.date && typeof data.date.toDate === 'function') {
      // Already a Firestore Timestamp
      timestamp = data.date;
    } else if (data.date instanceof Date) {
      timestamp = Timestamp.fromDate(data.date);
    } else if (typeof data.date === 'string' || typeof data.date === 'number') {
      const parsed = new Date(data.date);
      timestamp = isNaN(parsed.getTime()) ? Timestamp.now() : Timestamp.fromDate(parsed);
    } else {
      timestamp = Timestamp.now();
    }

    // Normalize "from" across legacy shapes:
    // - array of {address,name}
    // - string email
    // - single object {address,name,text}
    // - fallback to "sender" string
    let fromArray: any[] = []
    if (Array.isArray(data.from)) {
      fromArray = data.from
    } else if (typeof data.from === 'string') {
      const addr = normalizeEmailAddr(data.from)
      if (addr) fromArray = [{ address: addr }]
    } else if (data.from && typeof data.from === 'object') {
      const addr = normalizeEmailAddr((data.from as any).address || (data.from as any).text)
      const name = (data.from as any).name ? String((data.from as any).name) : undefined
      if (addr || name) fromArray = [{ address: addr || '', name }]
    } else if (typeof data.sender === 'string') {
      const addr = parseAddressFromText(data.sender)
      if (addr) fromArray = [{ address: addr }]
    }

    const primaryFrom = fromArray.length > 0 ? fromArray[0] : null;
    const senderEmail = (primaryFrom?.address ? String(primaryFrom.address).toLowerCase() : '') || undefined;
    // Normalize workRelated: prefer top-level if present (manual review may write here), else nested under llm
    const workRelated: boolean | null = (typeof data.workRelated === 'boolean')
      ? data.workRelated
      : (typeof data?.llm?.workRelated === 'boolean' ? data.llm.workRelated : null);
    const quarantined: boolean | null = (typeof data.quarantined === 'boolean') ? data.quarantined : null;
    const source: MailSource = normalizeMailSource(data?.source);
    const isRead: boolean | null = (typeof data.isRead === 'boolean') ? data.isRead : null;
    const mailbox: string | undefined = typeof data.mailbox === 'string' ? data.mailbox : undefined;
    const graph: any = data?.graph && typeof data.graph === 'object' ? data.graph : undefined;
    const hasAttachments: boolean | undefined = typeof data.hasAttachments === 'boolean' ? data.hasAttachments : undefined;
    const aiSummary: string | null = typeof data.aiSummary === 'string' ? data.aiSummary : null;
    const aiImportance: string | null = typeof data.aiImportance === 'string' ? data.aiImportance : null;

    // Map to custom virtual folder if rules match
    let virtualFolderId: string | undefined = undefined;
    if (senderEmail && (data.folder === 'inbox' || data.folder === 'junk' || !data.folder)) {
      const match = customFolders.find(f => f.senderEmails.includes(senderEmail.toLowerCase()));
      if (match) virtualFolderId = match.id;
    }

    // Normalize subject across legacy/variant shapes (Graph/IMAP/older docs)
    const subjectCandidate =
      data.subject ??
      data.title ??
      data.emailSubject ??
      data?.headers?.subject ??
      data?.envelope?.subject ??
      null;
    const subject = (subjectCandidate ? String(subjectCandidate) : '').trim() || 'No Subject';
    const senderFallback = (typeof data.sender === 'string' && data.sender.trim()) ? data.sender.trim() : "Unknown Sender";
    const senderDisplay = (Array.isArray(fromArray) && fromArray.length > 0 ? (fromArray[0].name || fromArray[0].address) : senderFallback) || senderFallback;

    // Normalize folder for the Inbox/Sent/Drafts/Junk/Deleted/Archive/Filed tabs. One shared rule
    // (emailFolder.ts): it also refuses to present deleted:true + folder:'inbox' (mail the owner moved
    // out of the inbox, mislabelled by older syncs) as an inbox row. `emailType` is the derived value
    // above so legacy direction:'out' docs still land in Sent.
    const folder: EmailFolder = normalizeEmailFolder({
      folder: data?.folder,
      deleted: data?.deleted,
      emailType,
      quarantined: data?.quarantined,
    })
    // Where filed mail actually lives in Outlook (only present on archive/filed docs).
    const outlookFolderId: string | undefined = typeof data.outlookFolderId === 'string' && data.outlookFolderId ? data.outlookFolderId : undefined
    const outlookFolderName: string | undefined = typeof data.outlookFolderName === 'string' && data.outlookFolderName ? data.outlookFolderName : undefined
    const outlookFolderPath: string | undefined = typeof data.outlookFolderPath === 'string' && data.outlookFolderPath ? data.outlookFolderPath : undefined
    const purged: boolean | undefined = data.purged === true ? true : undefined

    return {
      id: docSnap.id,
      emailId: docSnap.id,
      title: subject,
      subject: subject,
      message: data.text || "", // Snippet for feed - ensure it's brief
      text: data.text || "",
      html: data.html || null,
      createdAt: timestamp.toDate(),
      date: timestamp,
      dismissed: data.dismissed || false,
      deleted: data.deleted || false,
      purged,
      pinned: getEmailFlaggedState(data),
      sender: senderDisplay,
      emailType,
      // Raw ingest direction, carried so a reply can tell own-sent mail apart even
      // when `folder` says otherwise (own copies in inbox/deleted are all 'in').
      direction: typeof data.direction === 'string' ? data.direction : undefined,
      // Cast: FeedEmailData.folder is widened to EmailFolder separately; the runtime value is already right.
      folder: folder as FeedEmailData['folder'],
      outlookFolderId,
      outlookFolderName,
      outlookFolderPath,
      from: fromArray,
      to: Array.isArray(data.to) 
          ? data.to.map((recipient: any) => typeof recipient === 'string' ? recipient : recipient.address).filter(Boolean) 
          : (typeof data.to === 'string' ? [data.to] : []),
      cc: Array.isArray(data.cc) 
          ? data.cc.map((recipient: any) => typeof recipient === 'string' ? recipient : recipient.address).filter(Boolean) 
          : (typeof data.cc === 'string' ? [data.cc] : []),
      bcc: Array.isArray(data.bcc) 
          ? data.bcc.map((recipient: any) => typeof recipient === 'string' ? recipient : recipient.address).filter(Boolean) 
          : (typeof data.bcc === 'string' ? [data.bcc] : []),
      attachments: data.attachments || [],
      customerId: Array.isArray(data.customerId) ? data.customerId[0] : (data.customerId || null),
      // New fields used by classification UI
      workRelated,
      aiSummary,
      aiImportance,
      quarantined,
      senderEmail,
      source,
      isRead: isRead === null ? undefined : isRead,
      mailbox,
      graph: graph ? {
        id: graph.id || null,
        conversationId: graph.conversationId || null,
        flagStatus: graph.flagStatus || null,
      } : undefined,
      hasAttachments,
      virtualFolderId,
      // Work Hub fields
      dismissedFromWorkHub: data.dismissedFromWorkHub || false,
      linkedJobId: data.linkedJobId || null,
      linkedJobLabel: data.linkedJobLabel || null,
      // Waiting-on-them ledger opt-out (owner dismissed this sent email).
      waitingDismissed: data.waitingDismissed === true,
      // Snooze: the ms-epoch the email should resurface (client-side filtered).
      snoozedUntil: (() => {
        const v: any = (data as any).snoozedUntil;
        if (!v) return null;
        if (typeof v === 'number') return v;
        if (typeof v.toMillis === 'function') return v.toMillis();
        if (typeof v.seconds === 'number') return v.seconds * 1000;
        return null;
      })(),
    } as FeedEmailData;
  };

  // The feed listeners outlive any one render; they convert snapshots through the latest converter
  // (it closes over customFolders for virtual-folder matching).
  const convertDocRef = useRef(convertDocToFeedEmailData);
  convertDocRef.current = convertDocToFeedEmailData;

  const isEmailUnreadForBadge = (email: FeedEmailData): boolean => {
    // Legacy IMAP: dismissed is our read/unread
    if ((email.source || 'imap') !== 'graph') return !email.dismissed;
    // Graph: prefer isRead if present, fallback to dismissed (mirrored)
    if (typeof (email as any).isRead === 'boolean') return (email as any).isRead === false;
    return !email.dismissed;
  };

  const computeUnreadInboxCount = (emails: FeedEmailData[]): number => {
    return emails.filter((e) => {
      // 0) Must be part of the live inbox. preLoadedEmails also holds
      // search / historical hydration; gating on the live allowlist keeps
      // the badge counting only the live 30-day inbox. (size>0 guard so we
      // don't zero the badge before the first snapshot lands.)
      if (liveInboxIds.size > 0 && !liveInboxIds.has(e.id)) return false;

      // 1) Must be unread
      if (!isEmailUnreadForBadge(e)) return false;

      // 1b) Snoozed mail is intentionally out of sight until its wake time —
      // it must not keep inflating the badge (nor re-trigger the new-mail sound
      // when the snooze write bumps preLoadedEmails). Matches the feed filter.
      const snoozedUntil = (e as any).snoozedUntil;
      if (typeof snoozedUntil === 'number' && snoozedUntil > Date.now()) return false;

      // 2) Must be in physical Inbox/None. Junk is EXCLUDED — moving an email
      // to Junk should drop it from the unread badge (it previously stayed
      // counted, so junking didn't decrement the badge the way marking-read did).
      // The same check excludes Outlook's 'archive' / 'filed' folders (mail the owner moved out
      // of the inbox), and convertDocToFeedEmailData maps deleted:true + folder:'inbox' to 'deleted'.
      const physicalFolder = (e.folder || 'inbox');
      if (physicalFolder !== 'inbox' && (e as any).folder) return false;
      if (e.deleted === true) return false;
      if (e.emailType === 'sent') return false;

      // 3) Must NOT be in a virtual folder (user request: these appear directly in their folders)
      const senderAddr = e.from?.[0]?.address || (e as any).senderEmail;
      if (senderAddr) {
        const match = customFolders.find(f => 
          f.senderEmails.some(addr => addr.toLowerCase() === senderAddr.toLowerCase())
        );
        if (match) return false;
      }

      return true;
    }).length;
  };

  // Sound and badge reactivity
  useEffect(() => {
    const nextCount = computeUnreadInboxCount(preLoadedEmails);
    const prevCount = prevUnreadBadgeCountRef.current;
    
    setUnreadBadgeCount(nextCount);

    if (skipNextUnreadSoundRef.current) {
      prevUnreadBadgeCountRef.current = nextCount;
      skipNextUnreadSoundRef.current = false;
    } else if (typeof prevCount === 'number' && nextCount > prevCount) {
      playNewEmailSound().catch(() => undefined);
      prevUnreadBadgeCountRef.current = nextCount;
    } else {
      prevUnreadBadgeCountRef.current = nextCount;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preLoadedEmails, customFolders, liveInboxIds]);

  // Function to load older emails
  const loadOlderEmails = async (): Promise<void> => {
    if (isLoadingOlderEmails || !hasMoreOlderEmails || !oldestLoadedDate) {
      return;
    }

    setIsLoadingOlderEmails(true);
    
    try {
      console.log("[EmailPanelContext] Loading older emails before:", oldestLoadedDate.toDate());
      
      // Query for emails older than the oldest currently loaded
      // NOTE: We intentionally do not add a Firestore `where('source', ...)` here yet because legacy IMAP
      // docs may not have the field; we filter client-side via `mailSourceMode` for a safe transition.
      const olderEmailsQuery = query(
        collection(db, "emails"),
        where("date", "<", oldestLoadedDate),
        orderBy("date", "desc"),
        limit(50) // Load 50 older emails at a time (to account for filtering)
      );

      const olderEmailsSnapshot = await getDocs(olderEmailsQuery);
      const rawCount = olderEmailsSnapshot.docs.length;
      
      if (olderEmailsSnapshot.empty) {
        console.log("[EmailPanelContext] No more older emails found");
        setHasMoreOlderEmails(false);
        setIsLoadingOlderEmails(false);
        return;
      }

      // Get all raw emails first (before filtering) to track the oldest date
      const rawEmails = olderEmailsSnapshot.docs.map(convertDocToFeedEmailData);
      
      const olderEmails = filterEmailsBySourceMode(rawEmails, mailSourceMode);
      
      console.log("[EmailPanelContext] Loaded", olderEmails.length, "older emails (raw:", rawCount, ")");
      
      // Append older emails to the existing list (only if we have any after filtering)
      if (olderEmails.length > 0) {
        setPreLoadedEmails(prevEmails => appendOlderEmails(prevEmails, olderEmails));
      }
      
      // IMPORTANT: Update the oldest loaded date from RAW results, not filtered
      // This ensures we keep paginating even if all results were filtered out
      const newOldestDate = rawEmails[rawEmails.length - 1]?.date;
      if (newOldestDate) {
        setOldestLoadedDate(newOldestDate);
      }
      
      // Check if we've loaded less than requested from RAW results (indicates we've reached the end)
      // Only mark as "no more" when the database itself returns fewer than requested
      if (rawCount < 50) {
        setHasMoreOlderEmails(false);
      }
      
    } catch (error) {
      console.error("[EmailPanelContext] Error loading older emails:", error);
    } finally {
      setIsLoadingOlderEmails(false);
    }
  };

  // Live feed subscription: one listener PER MAILBOX over the last 30 days (see feedWindow.ts for why
  // and for all the merge / coverage / lifecycle logic - this effect only wires Firestore and state).
  useEffect(() => {
    console.log("[EmailPanelContext] Setting up Firestore listeners for initial emails and badge count.");
    setIsLoadingInitialEmails(true);
    // Avoid a sound on first snapshot / re-subscribe (e.g. source mode change)
    skipNextUnreadSoundRef.current = true;

    // Emails of the last 30 days. Includes deleted so Deleted tab can work.
    const thirtyDaysAgo = thirtyDayCutoff(new Date());
    const cutoffMs = thirtyDaysAgo.getTime();
    const cutoffTs = Timestamp.fromDate(thirtyDaysAgo);

    const coordinator = createFeedCoordinator<FeedEmailData, Timestamp>({
      cutoffMs,
      cursorOf: (email) => email.date,
      subscribe: (mailbox, limitN, handlers) => {
        // Same approach as before: broad query + client-side filter to avoid legacy-field issues during cutover.
        // mailbox === null is the legacy whole-collection query (the fallback path only).
        const feedQuery = mailbox === null
          ? query(
              collection(db, "emails"),
              where("date", ">=", cutoffTs),
              orderBy("date", "desc"),
              // Increased limit to ensure visibility during high activity periods
              limit(limitN)
            )
          // Uses the composite index emails(mailbox ASC, date DESC).
          : query(
              collection(db, "emails"),
              where("mailbox", "==", mailbox),
              where("date", ">=", cutoffTs),
              orderBy("date", "desc"),
              limit(limitN)
            );
        return onSnapshot(feedQuery, (snapshot) => {
          const all = snapshot.docs.map(convertDocRef.current);
          handlers.onSnapshot({
            emails: filterEmailsBySourceMode(all, mailSourceMode),
            // Judged BEFORE the source-mode filter: a filtered-out doc still used one of the limit slots.
            rawCount: all.length,
            oldestRaw: all.length > 0 ? all[all.length - 1] : null,
          });
        }, handlers.onError);
      },
      publish: (windows, firstOfSource) => {
        // Merge instead of replace. A window is authoritative ONLY inside its own coverage: the
        // per-mailbox queries are limited, so a busy mailbox may not reach back the full 30 days.
        // Search-loaded / paginated emails outside a mailbox's coverage must be preserved across
        // snapshots; anything newer than the coverage edge that is no longer returned was deleted
        // or moved out of the window, so it is dropped (see mergeWithExternal).
        const union = unionWindowEmails(windows);

        // The first snapshot of a source (initial load, a late-added mailbox, the legacy fallback)
        // is mail that was already there: never ring the new-mail sound for it.
        if (firstOfSource) skipNextUnreadSoundRef.current = true;

        setPreLoadedEmails(prevEmails => mergeWithExternal(prevEmails, union, windows));

        // Publish the live-inbox allowlist. Replaced wholesale every tick
        // so it's always exactly "what the canonical subscriptions cover
        // right now". The dashboard attention queue + unread badge gate on
        // this set, so search / historical emails merged into
        // preLoadedEmails (which are NOT in a snapshot) can never leak
        // into those surfaces. No manual bookkeeping needed — membership
        // tracks the live window automatically.
        setLiveInboxIds(union.ids);

        // Set the oldest loaded date for pagination based on the LIVE windows
        // (not the merged set) — pagination starts from the coverage boundary,
        // not from search-loaded outliers.
        if (union.emails.length > 0) {
          setOldestLoadedDate(paginationEdge(windows, cutoffMs).cursor || cutoffTs);
          // Reset hasMoreOlderEmails to true when we have initial data
          setHasMoreOlderEmails(true);
        }
      },
      initialLoaded: () => setIsLoadingInitialEmails(false),
      fallback: (reason) => {
        feedLegacyFallbackRef.current = true;
        const r: any = reason;
        console.warn(
          '[EmailPanelContext] Per-mailbox email feed unavailable (' + (r?.code || 'error') + '); using the single-query feed for the rest of this session.',
          r?.message || ''
        );
      },
      legacyError: (error) => {
        console.error("[EmailPanelContext] Error fetching pre-loaded emails:", error);
      },
    });
    feedCoordinatorRef.current = coordinator;
    // 'imap' mode shows legacy docs that predate the mailbox field: only the single query can see them.
    coordinator.start(feedMailboxesRef.current, { legacy: feedLegacyFallbackRef.current || mailSourceMode === 'imap' });

    return () => {
      console.log("[EmailPanelContext] Unsubscribing Firestore listeners.");
      // dispose() also guarantees no late snapshot/error callback touches state afterwards.
      coordinator.dispose();
      if (feedCoordinatorRef.current === coordinator) feedCoordinatorRef.current = null;
    };
  }, [mailSourceMode]); // Re-subscribe when source mode changes

  // The configured mailbox list changed: add/remove only the affected per-mailbox listeners.
  // (Declared after the effect above: on mount the coordinator already started with this same list.)
  useEffect(() => {
    feedCoordinatorRef.current?.setMailboxes(feedMailboxes);
  }, [feedMailboxes]);

  /** Resolve the current user ID from Firebase Auth (preferred) or localStorage fallback. */
  const getCurrentUserId = (): string | null => {
    return auth.currentUser?.uid || localStorage.getItem('user_uid') || null;
  };

  // Load user drafts on mount + subscribe for live updates.
  // The initial load also pulls Outlook (Graph) drafts into Firestore.
  // The snapshot subscription keeps the panel in sync when Steve (or any
  // other writer) creates/updates drafts — no manual refresh needed.
  useEffect(() => {
    const userId = getCurrentUserId();
    if (!userId) return;

    loadUserDrafts();

    // Equality-only (no orderBy): ordering by updatedAt dropped Outlook-synced drafts that lack the
    // field. Sorted client-side by updatedAt || syncedAt || createdAt inside mapDraftDocs.
    const unsub = onSnapshot(draftsQueryForUser(userId), (snap) => {
      setUserDrafts(mapDraftDocs(snap.docs));
    }, (err) => {
      console.error('[EmailPanelContext] drafts subscribe failed:', err);
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEmailPanel = (view: EmailPanelView, data?: PanelEmailData) => {
    setEmailPanelViewState(view);
    setCurrentEmailData(data || null);
    setIsEmailPanelOpen(true);
  };

  const closeEmailPanel = () => {
    setIsEmailPanelOpen(false);
    // Consider resetting view to 'feed' and clearing data if that's the desired UX upon every close
    // setEmailPanelViewState('feed'); 
    // setCurrentEmailData(null);
  };

  // Added to allow changing view and data while panel is already open
  const setEmailPanelView = (view: EmailPanelView, data?: PanelEmailData) => {
    setEmailPanelViewState(view);
    setCurrentEmailData(data || null);
    if (!isEmailPanelOpen) {
        setIsEmailPanelOpen(true); // Also open panel if it was closed
    }
  };

  // Light drafts-list refresh: local Firestore read ONLY. The full
  // loadUserDrafts() runs the Outlook (Graph) draft sync first — correct when
  // OPENING the drafts panel, but running it on every autosave meant each
  // keystroke-pause hammered Graph (mailbox throttling) and merge-wrote Graph's
  // normalized/stale copy back over the doc the user is actively editing —
  // "my changes keep reverting". Autosaves now refresh the list locally only.
  const refreshDraftsListLocal = async (): Promise<void> => {
    const userId = getCurrentUserId();
    if (!userId) return;
    try {
      const drafts = await readUserDraftsLocal(userId);
      setUserDrafts(drafts);
    } catch (e) {
      console.warn('[EmailPanelContext] local drafts refresh failed:', e);
    }
  };

  // Draft management functions
  const saveDraft = async (draftData: Partial<EmailDraft>): Promise<string> => {
    try {
      setDraftError(null);
      const userId = getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const draftId = await draftService.saveDraft(draftData, userId);
      // Refresh drafts list (local only — no Graph sync on the autosave path)
      await refreshDraftsListLocal();
      return draftId;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save draft';
      setDraftError(errorMessage);
      throw error;
    }
  };

  const updateDraft = async (draftId: string, updates: Partial<EmailDraft>): Promise<void> => {
    try {
      setDraftError(null);
      await draftService.updateDraft(draftId, updates);
      // Refresh drafts list (local only — no Graph sync on the autosave path)
      await refreshDraftsListLocal();
      // NOTE: deliberately NOT setCurrentDraft(...) here. currentDraft is the
      // composers' hydration source; replacing its identity on every autosave
      // re-fired their hydration effects MID-TYPING and reverted the editor to
      // the just-saved snapshot. currentDraft should only change when a draft
      // is deliberately OPENED (setDraftMode) — the composer owns live state.
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update draft';
      setDraftError(errorMessage);
      throw error;
    }
  };

  const deleteDraft = async (draftId: string): Promise<void> => {
    try {
      setDraftError(null);
      await draftService.deleteDraft(draftId);
      // Refresh drafts list
      await loadUserDrafts();
      // Clear current draft if it's the one being deleted
      if (currentDraft && currentDraft.id === draftId) {
        setCurrentDraft(null);
        setDraftModeState('none');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to delete draft';
      setDraftError(errorMessage);
      throw error;
    }
  };

  const loadUserDrafts = async (): Promise<void> => {
    // Guard: skip if a load is already in flight to prevent race conditions
    if (isLoadingDraftsRef.current) return;
    isLoadingDraftsRef.current = true;
    try {
      setIsLoadingDrafts(true);
      setDraftError(null);

      const userId = getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Pull recent Graph drafts into Firestore drafts before reading
      // (this makes Outlook-created drafts appear in the portal Drafts tab)
      try {
        await draftService.syncGraphDraftsForCurrentUser(30);
      } catch (e) {
        console.warn('[EmailPanelContext] Graph draft sync failed; continuing with local drafts', e);
      }

      const drafts = await readUserDraftsLocal(userId);
      setUserDrafts(drafts);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load drafts';
      setDraftError(errorMessage);
      console.error('Error loading user drafts:', error);
    } finally {
      setIsLoadingDrafts(false);
      isLoadingDraftsRef.current = false;
    }
  };

  const setDraftMode = (mode: 'new' | 'edit' | 'none', draft?: EmailDraft) => {
    setDraftModeState(mode);
    setCurrentDraft(draft || null);
    setDraftError(null);
  };

  const clearDraftError = () => {
    setDraftError(null);
  };

  // Load specific emails by their Firestore document IDs (for search results)
  const loadEmailsByIds = async (
    emailIds: string[],
  ): Promise<FeedEmailData[]> => {
    if (!emailIds || emailIds.length === 0) return [];
    
    try {
      console.log("[EmailPanelContext] Loading", emailIds.length, "emails by ID");
      
      // Firestore "in" queries are limited to 30 items, so batch them
      const BATCH_SIZE = 30;
      const allDocs: any[] = [];
      
      for (let i = 0; i < emailIds.length; i += BATCH_SIZE) {
        const batchIds = emailIds.slice(i, i + BATCH_SIZE);
        // Use documentId() filter or fetch each doc individually
        // For efficiency, fetch each doc in parallel within the batch
        const batchPromises = batchIds.map(async (id) => {
          try {
            const docRef = doc(db, "emails", id);
            const docSnap = await getDoc(docRef);
            return docSnap.exists() ? docSnap : null;
          } catch (e) {
            console.warn(`[EmailPanelContext] Failed to fetch email ${id}:`, e);
            return null;
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        allDocs.push(...batchResults.filter(d => d !== null));
      }
      
      const emails = filterEmailsBySourceMode(
        allDocs.map(convertDocToFeedEmailData),
        mailSourceMode
      );
      
      console.log("[EmailPanelContext] Loaded", emails.length, "emails by ID");
      
      // Add these emails to preLoadedEmails if not already there
      setPreLoadedEmails(prevEmails => {
        const existingIds = new Set(prevEmails.map(e => e.id));
        const newEmails = emails.filter(e => !existingIds.has(e.id));

        if (newEmails.length > 0) {
          const mergedEmails = [...prevEmails, ...newEmails].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          return mergedEmails;
        }

        return prevEmails;
      });

      // No allowlist bookkeeping needed: these hydrated emails are NOT in
      // the live snapshot, so they're automatically excluded from the
      // dashboard / badge by the liveInboxIds gate. If any of them are
      // genuinely in the live window, the canonical subscription already
      // covers them and will include their id in liveInboxIds.
      return emails;
    } catch (error) {
      console.error("[EmailPanelContext] Error loading emails by IDs:", error);
      return [];
    }
  };

  // New function for loading emails in a specific date range
  const loadEmailsForDateRange = async (startDate: Date, endDate: Date, customerId?: string): Promise<FeedEmailData[]> => {
    // Guard: ensure date range is valid before hitting Firestore
    if (startDate > endDate) {
      console.warn('[EmailPanelContext] loadEmailsForDateRange: startDate is after endDate — swapping to avoid invalid Firestore query', { startDate, endDate });
      [startDate, endDate] = [endDate, startDate];
    }
    try {
      console.log("[EmailPanelContext] Loading emails for date range:", startDate, "to", endDate, "for customer:", customerId);

      // Calculate date range in days to determine appropriate limits
      const rangeDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

      // IMPORTANT:
      // Date-range search windows can easily exceed 500 emails (especially 3+ months across multiple mailboxes).
      // If we only take the newest 500, keyword matches can be missed. So we page through results.
      // For very large ranges (>1 year), we need to be more aggressive but also set reasonable limits.
      const PAGE_SIZE = 500;
      // Scale MAX_PAGES based on date range, but cap at reasonable limits
      const MAX_PAGES = rangeDays > 365 ? 20 : rangeDays > 180 ? 15 : 10; // Up to 10,000 docs for 1yr+ ranges

      let lastDoc: any = null;
      let pages = 0;
      const allDocs: any[] = [];

      while (pages < MAX_PAGES) {
        // Build query for the date range
        let pageQuery;
        if (customerId) {
          pageQuery = query(
            collection(db, "emails"),
            where("customerId", "==", customerId),
            where("date", ">=", Timestamp.fromDate(startDate)),
            where("date", "<=", Timestamp.fromDate(endDate)),
            orderBy("date", "desc"),
            ...(lastDoc ? [startAfter(lastDoc)] : []),
            limit(PAGE_SIZE)
          );
        } else {
          pageQuery = query(
            collection(db, "emails"),
            where("date", ">=", Timestamp.fromDate(startDate)),
            where("date", "<=", Timestamp.fromDate(endDate)),
            orderBy("date", "desc"),
            ...(lastDoc ? [startAfter(lastDoc)] : []),
            limit(PAGE_SIZE)
          );
        }

        const snap = await getDocs(pageQuery);
        if (snap.empty) break;

        allDocs.push(...snap.docs);
        lastDoc = snap.docs[snap.docs.length - 1];
        pages += 1;

        // If we got fewer than a full page, we reached the end of the range.
        if (snap.docs.length < PAGE_SIZE) break;
      }

      if (allDocs.length === 0) {
        console.log("[EmailPanelContext] No emails found for date range");
        return [];
      }

      if (pages >= MAX_PAGES) {
        console.warn("[EmailPanelContext] loadEmailsForDateRange hit MAX_PAGES cap; results may be incomplete:", {
          startDate,
          endDate,
          customerId,
          pages,
          totalDocs: allDocs.length,
          rangeDays,
          maxPages: MAX_PAGES,
        });
      }

      const emails = filterEmailsBySourceMode(
        allDocs.map(convertDocToFeedEmailData),
        mailSourceMode
      );

      console.log("[EmailPanelContext] Loaded", emails.length, "emails for date range (raw docs:", allDocs.length, ", pages:", pages, ", range:", rangeDays, "days)");
      
      // Add these emails to the preLoadedEmails if they're not already there
      setPreLoadedEmails(prevEmails => {
        const existingIds = new Set(prevEmails.map(e => e.id));
        const newEmails = emails.filter(e => !existingIds.has(e.id));
        
        if (newEmails.length > 0) {
          // Merge and sort by date descending
          const mergedEmails = [...prevEmails, ...newEmails].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          return mergedEmails;
        }
        
        return prevEmails;
      });
      
      return emails;
      
    } catch (error) {
      console.error("[EmailPanelContext] Error loading emails for date range:", error);
      return [];
    }
  };

  return (
    <EmailPanelContext.Provider
      value={{
        isEmailPanelOpen,
        emailPanelView,
        currentEmailData,
        openEmailPanel,
        closeEmailPanel,
        setEmailPanelView,
        preLoadedEmails,
        unreadBadgeCount,
        isLoadingInitialEmails,
        isLoadingOlderEmails,
        hasMoreOlderEmails,
        loadOlderEmails,
        loadEmailsForDateRange,
        loadEmailsByIds,
        // Lifted search state (persists across feed mount/unmount)
        searchTerm,
        setSearchTerm: (term: string) => {
          setSearchTerm(term);
          // Clearing the term resets the canonical match set + hits.
          // (No search-loaded id set to clear anymore — the dashboard /
          // badge gate on the liveInboxIds allowlist, which is owned by
          // the canonical subscription and unaffected by search state.)
          // isDeepSearching stays as the in-flight callers' responsibility
          // to flip off when they complete or abort.
          if (!term.trim()) {
            setLastDeepSearchTerm('');
            setDeepSearchHits(null);
            setDeepSearchIds(null);
          }
        },
        isDeepSearching,
        setIsDeepSearching,
        lastDeepSearchTerm,
        setLastDeepSearchTerm,
        deepSearchHits,
        setDeepSearchHits,
        deepSearchIds,
        setDeepSearchIds,
        liveInboxIds,
        mailSourceMode,
        senderClassifications,
        setSenderClassification,
        customFolders,
        createCustomFolder,
        addSenderToFolder,
        removeSenderFromFolder,
        renameCustomFolder,
        setCustomFolderParent,
        deleteCustomFolder,
        // Draft management
        currentDraft,
        draftMode,
        userDrafts,
        isLoadingDrafts,
        draftError,
        saveDraft,
        updateDraft,
        deleteDraft,
        loadUserDrafts,
        setDraftMode,
        clearDraftError,
        activeSyncJob
      }}
    >
      {children}
    </EmailPanelContext.Provider>
  );
};

export const useEmailPanel = () => {
  const context = useContext(EmailPanelContext);
  if (context === undefined) {
    throw new Error('useEmailPanel must be used within an EmailPanelProvider');
  }
  return context;
};
